import {
  ParentLimitedResponse,
  PaymentLinkUserType,
  PaymentsService,
  StudentLimitedResponse,
} from "client/openapi";
import { Button, ButtonColor } from "components/Button";
import { useState } from "react";
import Notifications from "util/notifications";

/**
 * Ask a student or parent for a card or bank by text and/or email: a
 * message, which kinds to ask for, and how to send it. Used by the Payment
 * information dialog and by the People panel's "No card or bank" chip.
 */
export default function RequestPaymentMethodForm({
  orgId,
  student,
  parent,
  onSent,
}: {
  orgId: number;
  student?: Pick<StudentLimitedResponse, "id">;
  parent?: Pick<ParentLimitedResponse, "id">;
  /** After the request went out (the Onboarding step reloads its row). */
  onSent?: () => void;
}) {
  const [paymentMethodViaTextStatus, setPaymentMethodViaTextStatus] = useState<
    "pending" | "success" | "error" | null
  >(null);

  const [content, setContent] = useState<string>("");

  // New states for the checkboxes
  const [requestACH, setRequestACH] = useState<boolean>(true); // Banks checked by default
  const [requestCard, setRequestCard] = useState<boolean>(false); // Cards not checked by default

  const [sendSMS, setSendSMS] = useState<boolean>(true); // SMS checked by default
  const [sendEmail, setSendEmail] = useState<boolean>(false); // Email not checked by default

  const sendPaymentMethodRequestViaText = () => {
    setPaymentMethodViaTextStatus("pending");

    PaymentsService.generatePaymentLink({
      orgId: orgId,
      content: content,
      requestBody: {
        user_type: student
          ? PaymentLinkUserType.STUDENT
          : PaymentLinkUserType.PARENT,
        student_id: student?.id || null,
        parent_id: parent?.id || null,
        request_ach: requestACH,
        request_card: requestCard,
        send_request_via_text: sendSMS,
        send_request_via_email: sendEmail,
      },
    })
      .then((response) => {
        setPaymentMethodViaTextStatus("success");
        Notifications.success(sendSMS ? "Text sent!" : "Email sent!");
        onSent?.();
      })
      .catch((error) => {
        setPaymentMethodViaTextStatus("error");
        Notifications.error(
          "Could not send text! Does this person have a valid phone number on file?"
        );
      });
  };

  // Condition to disable the send button if no payment method type is selected
  const canSend =
    paymentMethodViaTextStatus !== "pending" &&
    paymentMethodViaTextStatus !== "success" &&
    (requestACH || requestCard) &&
    (sendSMS || sendEmail);

  return (
    <div className="mt-4 space-y-4">
      {/* Message Input */}
      <input
        type="text"
        className="w-full p-2 mb-2 rounded-lg border border-gray-300 bg-gray-50 focus:outline-none focus:border-purple-400 focus:ring-1 focus:ring-purple-400 text-sm"
        placeholder="Enter a message (optional)..."
        value={content}
        onChange={(e) => setContent(e.target.value)}
      />

      {/* Payment Method Type Checkboxes */}
      <div className="flex space-x-4 items-center">
        <label className="inline-flex items-center space-x-2 text-sm text-gray-700">
          <input
            type="checkbox"
            checked={requestACH}
            onChange={() => setRequestACH(!requestACH)}
            className="h-4 w-4 text-purple-600 bg-gray-100 border-gray-300 rounded focus:ring-purple-500"
          />
          <span>Link bank (easy and recommended)</span>
        </label>
        <label className="inline-flex items-center space-x-2 text-sm text-gray-700">
          <input
            type="checkbox"
            checked={requestCard}
            onChange={() => setRequestCard(!requestCard)}
            className="h-4 w-4 text-purple-600 bg-gray-100 border-gray-300 rounded focus:ring-purple-500"
          />
          <span>Link card</span>
        </label>
      </div>

      <div
        style={{
          display: "flex",
          gap: "1rem",
          marginTop: "1rem",
          marginBottom: "1rem",
        }}
      >
        <label style={{ display: "flex", alignItems: "center" }}>
          <input
            type="checkbox"
            checked={sendSMS}
            onChange={(e) => setSendSMS(e.target.checked)}
            style={{
              appearance: "none",
              width: "18px",
              height: "18px",
              border: "2px solid #4ade80",
              borderRadius: "4px",
              backgroundColor: sendSMS ? "#4ade80" : "#fff",
              cursor: "pointer",
            }}
          />
          <span style={{ marginLeft: 8 }}>SMS</span>
        </label>

        <label style={{ display: "flex", alignItems: "center" }}>
          <input
            type="checkbox"
            checked={sendEmail}
            onChange={(e) => setSendEmail(e.target.checked)}
            style={{
              appearance: "none",
              width: "18px",
              height: "18px",
              border: "2px solid #4ade80",
              borderRadius: "4px",
              backgroundColor: sendEmail ? "#4ade80" : "#fff",
              cursor: "pointer",
            }}
          />
          <span style={{ marginLeft: 8 }}>Email</span>
        </label>
      </div>

      <Button
        color={!canSend ? ButtonColor.GRAY : ButtonColor.GREEN}
        disabled={!canSend}
        onClick={() => sendPaymentMethodRequestViaText()}
      >
        Send
      </Button>
    </div>
  );
}
